import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Megaphone, Loader2, Send, User, Users, UsersRound } from "lucide-react";

/**
 * Compositor rápido de comunicado interno con selector triple
 * (Individual / Varios / Todos). Pensado para llamarse desde
 * SmartInsights, alertas, IA Site Coach, etc.
 *
 * Props:
 *  - prefill: { asunto, contenido, urgencia, requiere_acuse, requiere_firma }
 *  - onClose, onSent
 */
export default function ComunicadoCompositor({ prefill = {}, onClose, onSent }) {
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    destinoTipo: prefill.destinoTipo || "all",
    destIds: prefill.destIds || [],
    asunto: prefill.asunto || "",
    contenido: prefill.contenido || "",
    urgencia: prefill.urgencia || "alta",
    requiere_acuse: prefill.requiere_acuse ?? true,
    requiere_firma: prefill.requiere_firma ?? false,
  });
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleUser = (uid) => setForm((s) => ({
    ...s,
    destIds: s.destIds.includes(uid) ? s.destIds.filter((x) => x !== uid) : [...s.destIds, uid],
  }));

  useEffect(() => {
    api.get("/users").then((r) => setUsers((r.data || []).filter((u) => u.active !== false))).catch(() => {});
  }, []);

  const send = async () => {
    if (!form.asunto.trim()) { toast.error("Indica un asunto"); return; }
    if (!form.contenido.trim()) { toast.error("Escribe el contenido"); return; }
    let destinatarios = "all";
    if (form.destinoTipo === "individual") {
      if (form.destIds.length !== 1) { toast.error("Selecciona exactamente 1 destinatario"); return; }
      destinatarios = form.destIds;
    } else if (form.destinoTipo === "multiple") {
      if (form.destIds.length === 0) { toast.error("Selecciona al menos un destinatario"); return; }
      destinatarios = form.destIds;
    }
    setSaving(true);
    try {
      await api.post("/messages", {
        asunto: form.asunto.trim(),
        contenido: form.contenido,
        destinatarios,
        urgencia: form.urgencia,
        requiere_acuse: !!form.requiere_acuse,
        requiere_firma: !!form.requiere_firma,
      });
      toast.success("Comunicado enviado");
      onSent && onSent();
      onClose();
    } catch (e) {
      toast.error(formatErr(e));
    }
    setSaving(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      data-testid="comunicado-compositor"
      className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center p-4 bg-black/70 backdrop-blur overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        className="w-full max-w-2xl my-4 rounded-3xl shadow-2xl bg-[var(--legatum-surface)] border border-[var(--legatum-border)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 px-6 py-4 border-b border-[var(--legatum-border)] bg-[var(--legatum-surface)]/95 backdrop-blur flex items-center justify-between rounded-t-3xl">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center text-white shadow-md"
                 style={{ background: "linear-gradient(135deg, #A03060, #7A1F4C)" }}>
              <Megaphone className="h-5 w-5" />
            </div>
            <div>
              <div className="text-base font-extrabold">Comunicado interno</div>
              <div className="text-xs text-[var(--legatum-text-muted)] font-semibold">Notifica a quien necesites — individual, varios o toda la empresa.</div>
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={saving} data-testid="comunicado-compositor-close"
            className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-black/[0.04] dark:hover:bg-white/5 disabled:opacity-50">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-extrabold text-[var(--legatum-text-muted)]">¿A quién enviar? *</Label>
            <div className="mt-1 inline-flex rounded-lg border border-[var(--legatum-border)] bg-[var(--legatum-elevated)] p-1 gap-1 text-[10px] font-bold uppercase tracking-widest">
              {[
                { v: "individual", l: "Individual", Icon: User },
                { v: "multiple",   l: "Varios",     Icon: Users },
                { v: "all",        l: "Todos",      Icon: UsersRound },
              ].map(({ v, l, Icon }) => (
                <button key={v} type="button" onClick={() => upd("destinoTipo", v)}
                  data-testid={`comunicado-destino-${v}`}
                  className="px-2.5 py-1 rounded-md transition-colors inline-flex items-center gap-1"
                  style={form.destinoTipo === v
                    ? { background: "var(--legatum-accent)", color: "var(--legatum-accent-fg)" }
                    : { color: "var(--legatum-text-muted)" }}>
                  <Icon className="h-3 w-3" /> {l}
                </button>
              ))}
            </div>
          </div>

          {form.destinoTipo === "individual" && (
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-extrabold text-[var(--legatum-text-muted)]">Destinatario *</Label>
              <select
                value={form.destIds[0] || ""}
                onChange={(e) => upd("destIds", e.target.value ? [e.target.value] : [])}
                data-testid="comunicado-destinatario-individual"
                className="mt-1 h-9 w-full px-2 rounded-md border border-[var(--legatum-border)] bg-[var(--legatum-elevated)] text-sm">
                <option value="">— Selecciona —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{`${u.name || u.email} (${u.role})`}</option>
                ))}
              </select>
            </div>
          )}
          {form.destinoTipo === "multiple" && (
            <div data-testid="comunicado-destinatario-multi">
              <div className="flex items-center justify-between">
                <Label className="text-[10px] uppercase tracking-widest font-extrabold text-[var(--legatum-text-muted)]">Destinatarios ({form.destIds.length})</Label>
                <div className="flex gap-2 text-[10px] font-bold">
                  <button type="button" onClick={() => upd("destIds", users.map((u) => u.id))} className="text-[var(--legatum-accent)] hover:underline">Todos</button>
                  <button type="button" onClick={() => upd("destIds", [])} className="text-[var(--legatum-text-muted)] hover:underline">Ninguno</button>
                </div>
              </div>
              <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-[var(--legatum-border)] bg-[var(--legatum-elevated)] divide-y divide-[var(--legatum-border)]">
                {users.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-black/[0.03] dark:hover:bg-white/[0.04]">
                    <Checkbox checked={form.destIds.includes(u.id)} onCheckedChange={() => toggleUser(u.id)} />
                    <span className="text-sm">{u.name || u.email} <span className="text-[10px] text-[var(--legatum-text-muted)] uppercase font-bold ml-1">{u.role}</span></span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {form.destinoTipo === "all" && (
            <div className="rounded-lg border border-[var(--legatum-accent)]/40 bg-[var(--legatum-accent)]/[0.08] px-3 py-2 text-xs text-[var(--legatum-text-secondary)]">
              Se enviará a los <b className="text-[var(--legatum-text)]">{users.length}</b> usuarios activos.
            </div>
          )}

          <div>
            <Label className="text-[10px] uppercase tracking-widest font-extrabold text-[var(--legatum-text-muted)]">Asunto *</Label>
            <Input className="mt-1" value={form.asunto} onChange={(e) => upd("asunto", e.target.value)}
              data-testid="comunicado-asunto" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest font-extrabold text-[var(--legatum-text-muted)]">Contenido *</Label>
            <textarea rows={7} value={form.contenido} onChange={(e) => upd("contenido", e.target.value)}
              data-testid="comunicado-contenido"
              className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--legatum-border)] bg-[var(--legatum-elevated)] text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#A03060]/40" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-extrabold text-[var(--legatum-text-muted)]">Urgencia</Label>
              <select value={form.urgencia} onChange={(e) => upd("urgencia", e.target.value)}
                data-testid="comunicado-urgencia"
                className="mt-1 h-9 w-full px-2 rounded-md border border-[var(--legatum-border)] bg-[var(--legatum-elevated)] text-sm">
                <option value="normal">Normal</option>
                <option value="alta">Alta</option>
                <option value="muy_alta">Muy alta</option>
              </select>
            </div>
            <label className="flex items-end gap-2 pb-1">
              <Checkbox checked={form.requiere_acuse} onCheckedChange={(v) => upd("requiere_acuse", !!v)} data-testid="comunicado-acuse" />
              <span className="text-xs font-bold">Requiere acuse</span>
            </label>
            <label className="flex items-end gap-2 pb-1">
              <Checkbox checked={form.requiere_firma} onCheckedChange={(v) => upd("requiere_firma", !!v)} data-testid="comunicado-firma" />
              <span className="text-xs font-bold">Requiere firma</span>
            </label>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[var(--legatum-border)] flex justify-end gap-2 bg-[var(--legatum-surface)]/95 backdrop-blur rounded-b-3xl">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={send} disabled={saving} data-testid="comunicado-enviar"
            className="bg-[#A03060] hover:bg-[#7A1F4C] text-white">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Enviar comunicado
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}
